import { useState } from 'react'
import { LayoutGrid, Pencil, Plus, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAllowedWidgets, useHomeDashboard } from '@/hooks/useDashboardLayouts'
import type { WidgetView } from '@/lib/dashboardWidgets'
import {
  defaultHomeWidgets,
  mergeHiddenWidgets,
  setWidgetView,
  type WidgetPlacement,
} from '@/lib/dashboardLayouts'
import { AddWidgetsDialog } from './AddWidgetsDialog'
import { isWidgetBuilt } from './builtWidgets'
import { DashboardGrid } from './DashboardGrid'
import { DashboardPeriodProvider, DashboardPeriodSelect } from './DashboardPeriod'
import { SaveFailedNote } from './WidgetCard'

/**
 * The widgets on Home (z8nrz7eqyv): the user's own set, or the default until
 * they customise it. Per user and per tenant, stored apart from both the
 * named dashboards and the Home tile layouts.
 *
 * Permission HIDES: a widget the role cannot see is never rendered, even from
 * a saved layout, and is kept (not dropped) when the layout is saved again, so
 * it comes back by itself if the permission does.
 */
export function HomeDashboard() {
  const home = useHomeDashboard()
  const { allowed, isAllowed } = useAllowedWidgets()
  const [draft, setDraft] = useState<WidgetPlacement[] | null>(null)
  const [picking, setPicking] = useState(false)

  const isDefault = home.widgets === null
  const stored = home.widgets ?? defaultHomeWidgets(isAllowed, isWidgetBuilt)
  const editing = draft !== null
  const shown = (draft ?? stored).filter((w) => isAllowed(w.id))

  const startEditing = () => setDraft(stored.filter((w) => isAllowed(w.id)))
  const cancel = () => {
    setDraft(null)
    setPicking(false)
  }
  const save = () => {
    if (draft) home.save(mergeHiddenWidgets(draft, home.widgets ?? [], isAllowed))
    cancel()
  }
  // A view switch outside edit mode saves at once. On the default set it makes
  // Home the user's own, exactly as Save would.
  const changeView = (id: string, view: WidgetView) =>
    home.save(mergeHiddenWidgets(setWidgetView(shown, id, view), home.widgets ?? [], isAllowed))
  const reset = () => {
    home.reset()
    cancel()
  }

  return (
    <DashboardPeriodProvider>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <DashboardPeriodSelect />
        {editing ? (
          <div className='flex items-center gap-2'>
            <Button size='sm' variant='outline' onClick={() => setPicking(true)}>
              <Plus className='mr-1 h-3.5 w-3.5' /> Add widgets
            </Button>
            <Button
              size='sm'
              variant='ghost'
              onClick={reset}
              disabled={isDefault}
              title={isDefault ? 'Home already shows the default widgets' : 'Go back to the default widgets'}
            >
              <RotateCcw className='mr-1 h-3.5 w-3.5' /> Reset
            </Button>
            <Button size='sm' variant='ghost' onClick={cancel}>
              Cancel
            </Button>
            <Button size='sm' onClick={save} disabled={!home.ready}>
              Save
            </Button>
          </div>
        ) : (
          <Button size='sm' variant='outline' onClick={startEditing} disabled={!home.ready}>
            <Pencil className='mr-1 h-3.5 w-3.5' /> Customize
          </Button>
        )}
      </div>

      {home.saveFailed && <SaveFailedNote />}

      {editing && (
        <p className='mb-3 text-xs text-gray-500'>
          Drag a widget by its handle to move it, choose S, W or F to resize it, and − to remove it.
        </p>
      )}

      {home.isLoading ? null : shown.length > 0 ? (
        <DashboardGrid
          widgets={shown}
          editing={editing}
          onChange={setDraft}
          onViewChange={home.ready ? changeView : undefined}
        />
      ) : (
        <EmptyHome onAdd={() => (editing ? setPicking(true) : (startEditing(), setPicking(true)))} />
      )}

      {editing && (
        <AddWidgetsDialog
          open={picking}
          onOpenChange={setPicking}
          draft={draft}
          allowed={allowed}
          onChange={setDraft}
        />
      )}
    </DashboardPeriodProvider>
  )
}

function EmptyHome({ onAdd }: { onAdd: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center'>
      <LayoutGrid className='h-8 w-8 text-gray-300' />
      <div>
        <p className='text-sm font-medium text-gray-700'>No widgets on Home</p>
        <p className='text-xs text-gray-400'>Add widgets to see the numbers that matter to you.</p>
      </div>
      <Button size='sm' onClick={onAdd}>
        <Plus className='mr-1 h-3.5 w-3.5' /> Add widgets
      </Button>
    </div>
  )
}
